import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractForm } from '../abstract-form.model';
import { ErzeugungsanlageConfig } from '../erzeugungsanlage.config';
import { AbstractControl, UntypedFormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { FormGroupConfig } from '../form-config.model';
import { Feld } from '../../../domain/model/feld.enum';
import { ResourceIds } from '../../../domain/model/resource-ids.model';
import { PredicateValidator } from '../validator/predicate.validator';
import {
    FunkrundsteuerEmpfaengerAbregelmoeglichkeit,
} from '../../../domain/model/funkrundsteuer-empfaenger-abregelmoeglichkeit.model';
import { ResourceIdValidator } from '../validator/resource-id.validator';
import { ResourceIdType } from '../../../domain/model/resource-id-type.enum';
import { TechnischeResourceIdUnique } from '../validator/technischeResource-id-unique.validator';
import { SteuergruppeIdNetzzuordnungValidator } from '../validator/steuergruppe-id-netzzuordnung.validator';
import {
    FunkrundsteuerEmpfaengerInformationen,
} from '../../../domain/model/funkrundsteuer-empfeanger-informationen.model';
import { SteuerbareResourceIdValidator } from '../validator/steuerbare-resource-id.validator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'redispatch-resource-ids-sub-form',
    templateUrl: './resource-ids-sub-form.component.html',
    styleUrls: [
        './resource-ids-sub-form.component.scss',
    ],
    providers: [
        SteuerbareResourceIdValidator,
        TechnischeResourceIdUnique,
        SteuergruppeIdNetzzuordnungValidator,
    ],
    standalone: false,
})
export class ResourceIdsSubFormComponent extends AbstractForm<ResourceIds> implements OnInit, OnChanges, OnDestroy {
    public static readonly SHARED_SR_ID_VALIDATOR = ResourceIdValidator.validate(ResourceIdType.STEUERBARE_RESOURCE_ID);
    public static readonly SHARED_SG_ID_VALIDATOR = ResourceIdValidator.validate(ResourceIdType.STEUERGRUPPE_ID);
    public static readonly ABREGELMOEGLICHKEITEN_FOR_REQUIRED_SR_ID = [
        FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FRE,
        FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FWA,
        FunkrundsteuerEmpfaengerAbregelmoeglichkeit.TASE2,
        FunkrundsteuerEmpfaengerAbregelmoeglichkeit.IEC101_104,
        FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FALLBACK_PROCESS_BY_PHONE,
    ];

    @Input({ required: true }) readonly: boolean;
    @Input({ required: true }) dokumentenId?: string;
    @Input({ required: true }) netzzuordnungId?: string;
    @Input({ required: true }) funkrundsteuerEmpfaengerInformationen?: FunkrundsteuerEmpfaengerInformationen;
    @Input({ required: true }) abregelmoeglichkeit?: FunkrundsteuerEmpfaengerAbregelmoeglichkeit;

    protected readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;
    protected readonly Feld = Feld;

    private steuerbareResourceIdValidator = inject(SteuerbareResourceIdValidator);
    private technischeResourceIdUnique = inject(TechnischeResourceIdUnique);
    private steuergruppeIdNetzzuordnungValidator = inject(SteuergruppeIdNetzzuordnungValidator);

    private onDestroy$ = new Subject<void>();

    constructor() {
        super(inject(UntypedFormBuilder));
    }

    get formGroupConfig(): FormGroupConfig<ResourceIds> {
        return {
            steuerbareResourceId: [
                { value: null, disabled: this.readonly },
                {
                    validators: [
                        PredicateValidator.conditionalValidator(
                            () => this.isSteuerbareResourceIdRequired,
                            Validators.required,
                        ),
                        PredicateValidator.conditionalValidator(
                            () => this.isSteuerbareResourceIdRequired,
                            ResourceIdValidator.validate(ResourceIdType.STEUERBARE_RESOURCE_ID),
                        ),
                        PredicateValidator.conditionalValidator(
                            () => !this.isSteuerbareResourceIdRequired,
                            this.nullValidator(),
                        ),
                    ],
                    asyncValidators: [
                        this.steuerbareResourceIdValidator.validate(
                            this.netzzuordnungId,
                            this.funkrundsteuerEmpfaengerInformationen,
                            null,
                            this.dokumentenId,
                        ),
                    ],
                    updateOn: 'change',
                },
            ],
            technischeResourceId: [
                { value: null, disabled: this.readonly },
                {
                    validators: [
                        PredicateValidator.conditionalValidator(
                            () => this.isTechnischeResourceIdRequired,
                            Validators.required,
                        ),
                        ResourceIdValidator.validate(ResourceIdType.TECHNISCHE_RESOURCE_ID),
                    ],
                    asyncValidators: [
                        this.technischeResourceIdUnique.validate(this.dokumentenId),
                    ],
                    updateOn: 'change',
                },
            ],
            steuergruppeId: [
                { value: null, disabled: this.readonly },
                {
                    validators: [
                        PredicateValidator.conditionalValidator(
                            () => this.isSteuerIdsNullRequired,
                            this.nullValidator(),
                        ),
                        PredicateValidator.conditionalValidator(
                            () => !this.isSteuerIdsNullRequired,
                            ResourceIdsSubFormComponent.SHARED_SG_ID_VALIDATOR,
                        ),
                    ],
                    asyncValidators: [
                        this.steuergruppeIdNetzzuordnungValidator.validate(this.netzzuordnungId, this.dokumentenId),
                    ],
                    updateOn: 'change',
                },
            ],
        };
    }

    get isSteuerbareResourceIdRequired(): boolean {
        return !this.isSteuerIdsNullRequired && (ResourceIdsSubFormComponent.ABREGELMOEGLICHKEITEN_FOR_REQUIRED_SR_ID
            .includes(this.abregelmoeglichkeit) ||
            !!this.f.steuergruppeId.value);
    }

    get isTechnischeResourceIdRequired(): boolean {
        return this.isSteuerbareResourceIdRequired;
    }

    get isSteuerIdsNullRequired(): boolean {
        return !ResourceIdsSubFormComponent.ABREGELMOEGLICHKEITEN_FOR_REQUIRED_SR_ID
            .includes(this.abregelmoeglichkeit);
    }

    ngOnInit(): void {
        this.f.steuergruppeId
            .valueChanges
            .pipe(
                takeUntil(this.onDestroy$),
            )
            .subscribe(() => this.updateSteuerbareResourceIdValidator());
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.form) {
            if (changes.netzzuordnungId || changes.funkrundsteuerEmpfaengerInformationen || changes.dokumentenId) {
                this.updateSteuerbareResourceIdValidator();
            }
            if (changes.abregelmoeglichkeit) {
                this.f.steuerbareResourceId.updateValueAndValidity();
                this.f.technischeResourceId.updateValueAndValidity();
                this.f.steuergruppeId.updateValueAndValidity();
            }
            if (changes.dokumentenId) {
                this.f.technischeResourceId.setAsyncValidators(
                    this.technischeResourceIdUnique.validate(this.dokumentenId),
                );
                this.f.technischeResourceId.updateValueAndValidity();
            }
            if (changes.netzzuordnungId || changes.dokumentenId) {
                this.f.steuergruppeId.setAsyncValidators(
                    this.steuergruppeIdNetzzuordnungValidator.validate(this.netzzuordnungId, this.dokumentenId),
                );
                this.f.steuergruppeId.updateValueAndValidity();
            }
        }
    }

    public sanitize(resourceIds: ResourceIds): ResourceIds {
        return resourceIds;
    }

    private updateSteuerbareResourceIdValidator(): void {
        this.f.steuerbareResourceId.setAsyncValidators(
            this.steuerbareResourceIdValidator.validate(
                this.netzzuordnungId,
                this.funkrundsteuerEmpfaengerInformationen,
                this.f.steuergruppeId.value,
                this.dokumentenId,
            ),
        );
        this.f.steuerbareResourceId.updateValueAndValidity();
    }

    private nullValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            if (control.value) {
                return { nullRequired: true };
            }
            return null;
        };
    }
}
